/**
 * Email validation utilities
 * Provides functions for validating email addresses and checking allowed domains
 */

/**
 * Allowed email domains for the application
 * Add new domains here when they are approved for access
 */
export const ALLOWED_EMAIL_DOMAINS = [
  'petronas.com',
  'gentari.com',
  'gentari.co.in'
] as const;

/**
 * Validates email format using regex
 * @param email - Email address to validate
 * @returns true if email format is valid, false otherwise
 */
export const isValidEmailFormat = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Extracts the domain from an email address
 * @param email - Email address
 * @returns domain part of the email or empty string if invalid format
 */
export const getEmailDomain = (email: string): string => {
  const parts = email.split('@');
  return parts.length === 2 ? parts[1].toLowerCase() : '';
};

/**
 * Checks if an email domain is in the allowed list
 * @param email - Email address to check
 * @returns true if the email domain is allowed, false otherwise
 */
export const isAllowedEmailDomain = (email: string): boolean => {
  const domain = getEmailDomain(email);
  return ALLOWED_EMAIL_DOMAINS.includes(domain as typeof ALLOWED_EMAIL_DOMAINS[number]);
};

/**
 * Validates email address including format and domain
 * @param email - Email address to validate
 * @returns Object with validation result and error message if invalid
 */
export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateEmail = (email: string): EmailValidationResult => {
  if (!email) {
    return {
      isValid: false,
      error: 'Please enter your email'
    };
  }

  if (!isValidEmailFormat(email)) {
    return {
      isValid: false,
      error: 'Incorrect email ID. Please enter a valid email ID'
    };
  }

  if (!isAllowedEmailDomain(email)) {
    const domainsList = ALLOWED_EMAIL_DOMAINS.map(d => `@${d}`).join(', ');
    return {
      isValid: false,
      error: `Access restricted to ${domainsList} email addresses only`
    };
  }

  return {
    isValid: true
  };
};

/**
 * Gets a formatted string of allowed domains for display in UI
 * @returns Formatted string of allowed domains
 */
export const getAllowedDomainsDisplay = (): string => {
  return ALLOWED_EMAIL_DOMAINS.map(d => `@${d}`).join(', ');
};
